import { Download, Eye, AlertTriangle, AlertCircle, Loader2, PersonStanding, Car, Flag } from "lucide-react";
import { toast } from "sonner";
import InjuryBadge from "./InjuryBadge";
import { openPdf, pdfUrl } from "@/lib/api";
import clsx from "clsx";

function handleDownload(report) {
  const r = openPdf(report.id);
  if (r.method === "new-tab") {
    toast.success("PDF opened in a new tab");
  } else if (r.method === "same-tab") {
    toast.success("Downloading…");
  } else if (r.method === "clipboard") {
    toast.message("PDF URL copied — paste it in a new browser tab to view.");
  } else {
    toast.error("Could not open PDF. Right-click the button and choose 'Open link in new tab'.");
  }
}

function isQualifying(p) {
  if (p.qualifying === true) return true;
  // Backward-compat for older persisted rows that lack the flag.
  const c = (p.injury_code || "").trim();
  if (c === "01" || c === "02" || c === "03") return true;
  if (c === "04" && p.transported) return true;
  return false;
}

function highestCode(people = []) {
  // Kept for potential future re-use; the column it powered was removed
  // because the per-person badges in the People column already show
  // severity per row.
  const rank = { "01": 5, "02": 4, "03": 3, "04": 2, "00": 0 };
  let best = null;
  let bestRank = -1;
  let bestTransported = false;
  for (const p of people) {
    if (!isQualifying(p)) continue;
    const r = rank[(p.injury_code || "").trim()] ?? -1;
    if (r > bestRank) {
      bestRank = r;
      best = (p.injury_code || "").trim();
      bestTransported = !!p.transported;
    }
  }
  return { code: best, transported: bestTransported };
}

function InsuranceCell({ insurance, reportId }) {
  const entries = (insurance || []).filter((i) => i && typeof i === "object");
  if (!entries.length) {
    return (
      <div data-testid={`row-insurance-${reportId}`}>
        <span
          data-testid={`no-insurance-flag-${reportId}`}
          className="inline-flex items-center gap-1 bg-[#C1121F] text-white border border-black px-1.5 py-[2px] font-mono text-[10px] uppercase tracking-[0.15em] font-bold"
        >
          <AlertCircle className="w-3 h-3" /> No Insurance — Review
        </span>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-red-700 font-bold">
          No carrier found
        </div>
      </div>
    );
  }
  return (
    <ul className="space-y-1" data-testid={`row-insurance-${reportId}`}>
      {entries.map((ins, i) => {
        const status = (ins.status || "").trim();
        const party = ins.party || `V${i + 1}`;
        if (!(ins.carrier || "").trim()) {
          return (
            <li key={i} className="font-mono text-[11px] leading-tight text-red-700 font-bold">
              <span className="font-bold">{party}</span> NO CARRIER FOUND
            </li>
          );
        }
        return (
          <li key={i} className="font-mono text-[11px] leading-tight">
            <span className="font-bold">{party}</span>
            {` ${ins.carrier}`}
            {ins.policy_no ? ` · #${ins.policy_no}` : ""}
            {status && (
              <span
                className={clsx(
                  "ml-1.5 border px-1 py-[1px] text-[9px] uppercase tracking-[0.12em] font-bold",
                  status.toLowerCase() === "uninsured"
                    ? "border-red-600 text-red-700 bg-red-50"
                    : status.toLowerCase() === "insured"
                    ? "border-black text-black bg-zinc-100"
                    : "border-zinc-400 text-zinc-500"
                )}
              >
                {status}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function CategoryGlyph({ category }) {
  if (category === "non_motorist") {
    return (
      <span
        title="Non-motorist"
        className="inline-flex items-center justify-center w-5 h-5 border border-black"
      >
        <PersonStanding className="w-3.5 h-3.5" strokeWidth={2} />
      </span>
    );
  }
  return (
    <span
      title="Occupant"
      className="inline-flex items-center justify-center w-5 h-5 border border-black/40"
    >
      <Car className="w-3.5 h-3.5 opacity-70" strokeWidth={2} />
    </span>
  );
}

export default function ReportTable({ reports, onOpen, loading }) {
  if (loading) {
    return (
      <div className="border border-black p-10 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!reports.length) {
    return (
      <div className="border border-black p-10 text-center">
        <AlertTriangle className="w-6 h-6 mx-auto mb-3" />
        <p className="font-mono text-xs uppercase tracking-[0.25em] font-semibold">
          No qualifying reports yet
        </p>
        <p className="text-sm opacity-70 mt-2">
          Upload PDFs or a ZIP. We surface any report where a driver, occupant or pedestrian is injured at level 01/02/03,
          or level 04 with hospital transport.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-black overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="reports-table">
          <thead>
            <tr className="border-b-2 border-black bg-zinc-50">
              <th className="text-left font-mono text-[11px] uppercase tracking-[0.2em] py-3 px-4">File</th>
              <th className="text-left font-mono text-[11px] uppercase tracking-[0.2em] py-3 px-4">Case&nbsp;/&nbsp;Date</th>
              <th className="text-left font-mono text-[11px] uppercase tracking-[0.2em] py-3 px-4">People (Qualifying)</th>
              <th className="text-left font-mono text-[11px] uppercase tracking-[0.2em] py-3 px-4 min-w-[220px]">Fault / Liability Notes</th>
              <th className="text-left font-mono text-[11px] uppercase tracking-[0.2em] py-3 px-4 min-w-[200px]">Insurance</th>
              <th className="text-right font-mono text-[11px] uppercase tracking-[0.2em] py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r, idx) => {
              const qualifying = r.people.filter(isQualifying);
              const others = r.people.length - qualifying.length;
              return (
                <tr
                  key={r.id}
                  data-testid={`report-row-${r.id}`}
                  className={clsx(
                    "border-b border-zinc-200 row-in align-top",
                    idx % 2 === 1 && "bg-zinc-50/60"
                  )}
                  style={{ animationDelay: `${Math.min(idx, 12) * 20}ms` }}
                >
                  <td className="py-3 px-4 font-mono text-[12px] max-w-[260px] truncate">
                    <div className="flex items-center gap-1.5">
                      {r.needs_review && (
                        <span
                          data-testid={`needs-review-${r.id}`}
                          title={`Possible missing person. Gemini reported ${r.expected_people_count ?? "?"} but extracted ${r.people.length}. Open the original PDF and confirm manually.`}
                          className="inline-flex items-center gap-1 bg-[#FFE066] text-black border border-black px-1.5 py-[1px] font-mono text-[10px] uppercase tracking-[0.18em] font-bold shrink-0"
                        >
                          <Flag className="w-3 h-3" /> Review
                        </span>
                      )}
                      <span className="truncate">{r.file_name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 font-mono text-[12px]">
                    <div>{r.case_no || "—"}</div>
                    <div className="opacity-60">{r.crash_date || ""}</div>
                  </td>
                  <td className="py-3 px-4">
                    <ul className="space-y-1.5">
                      {qualifying.map((p, i) => (
                        <li key={`${r.id}-${i}-${p.injury_code || "x"}`} className="flex items-center gap-2 flex-wrap">
                          <CategoryGlyph category={p.category} />
                          <InjuryBadge code={p.injury_code} transported={p.transported} />
                          <span className="font-mono text-[12px]">{p.name || "Unknown"}</span>
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] opacity-50">
                            {p.category === "non_motorist"
                              ? (p.non_motorist_type || "Non-Motorist")
                              : (p.person_type_label || "")}
                            {p.seat ? ` · ${p.seat}` : ""}
                          </span>
                          {p.transported && (
                            <span className="font-mono text-[10px] uppercase tracking-[0.15em] opacity-70 truncate max-w-[280px]">
                              → {p.medical_facility_name || p.ems_service_name}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {others > 0 && (
                      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] opacity-50">
                        +{others} non-qualifying on report
                      </div>
                    )}
                  </td>
                  <td
                    className="py-3 px-4 text-[12px] max-w-[260px] whitespace-normal align-top"
                    title={r.fault_notes || ""}
                    data-testid={`row-fault-${r.id}`}
                  >
                    {r.fault_notes ? r.fault_notes : <span className="opacity-40">—</span>}
                  </td>
                  <td className="py-3 px-4 max-w-[260px] whitespace-normal align-top">
                    <InsuranceCell insurance={r.insurance_info} reportId={r.id} />
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap align-top">
                    <button
                      data-testid={`view-report-${r.id}`}
                      onClick={() => onOpen(r)}
                      className="inline-flex items-center gap-1 border border-black bg-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-black hover:text-white transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    <a
                      data-testid={`download-report-${r.id}`}
                      href={pdfUrl(r.id)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownload(r);
                      }}
                      className="inline-flex items-center gap-1 border border-black bg-black text-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] ml-2 hover:bg-zinc-800 transition-colors no-underline cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" /> PDF
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
